import { parseBoundaryGeoJson } from './boundary'

/** Encode domain for report API path: dots → underscores (e.g. aledotx.gov → aledotx_gov) */
export const encodeDomainForReport = (domain) =>
  String(domain ?? '').replace(/\./g, '_')

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return null
}

const parseCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const normalizeOfficialDomain = (domain) =>
  String(domain ?? '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')

const isWikipediaUrl = (url) => {
  try {
    const host = new URL(url).hostname.toLowerCase()
    return host === 'wikipedia.org' || host.endsWith('.wikipedia.org')
  } catch {
    return /wikipedia\.org/i.test(String(url ?? ''))
  }
}

/** websites from API, or https://{official_domain} when absent */
export const resolveReportWebsites = (websites, officialDomain) => {
  const list = Array.isArray(websites)
    ? websites.map((url) => String(url).trim()).filter(Boolean)
    : []
  if (list.length > 0) return list

  const domain = normalizeOfficialDomain(officialDomain)
  return domain ? [`https://${domain}`] : []
}

/** Convert OSM wikipedia tag (e.g. "en:Aledo, Texas") into an article URL. */
const wikipediaUrlFromOsmTag = (value) => {
  const match = String(value ?? '')
    .trim()
    .match(/^([a-z]{2,3}):(.+)$/i)
  if (!match) return null
  const lang = match[1].toLowerCase()
  const title = match[2].trim().replace(/ /g, '_')
  if (!title) return null
  return `https://${lang}.wikipedia.org/wiki/${title}`
}

/** Build a Wikidata redirect that resolves to the linked Wikipedia article for a Q ID. */
const wikipediaUrlFromWikidataQId = (value, wiki = 'enwiki') => {
  const qid = String(value ?? '').trim().toUpperCase()
  if (!/^Q[1-9]\d*$/.test(qid)) return null
  return `https://www.wikidata.org/wiki/Special:GoToLinkedPage/${wiki}/${qid}`
}

/**
 * Prefer, in order: an explicit full Wikipedia URL, a Wikidata Q ID redirect,
 * an OSM-style wikipedia tag, or the first Wikipedia URL already in websites.
 */
export const resolveWikipediaUrl = (report, websites = []) => {
  const explicit = firstNonEmpty(
    report?.wikipediaUrl,
    report?.wikipedia_url,
    report?.wikipedia,
  )
  if (explicit && /^https?:\/\//i.test(explicit)) return explicit

  const wikidataQId = firstNonEmpty(report?.wikidataQId, report?.wikidata_q_id)
  const fromWikidata = wikipediaUrlFromWikidataQId(wikidataQId)
  if (fromWikidata) return fromWikidata

  const fromOsm = wikipediaUrlFromOsmTag(explicit)
  if (fromOsm) return fromOsm

  return websites.find(isWikipediaUrl) || null
}

const hasContactFields = (contact) =>
  firstNonEmpty(contact?.name, contact?.email, contact?.phone, contact?.title)

export const parseCrawlReport = (report, officialDomain = '') => {
  const latitude = parseCoordinate(report?.latitude)
  const longitude = parseCoordinate(report?.longitude)
  const allWebsites = resolveReportWebsites(report?.websites, officialDomain)
  const wikipediaUrl = resolveWikipediaUrl(report, allWebsites)
  const websites = allWebsites.filter((url) => !isWikipediaUrl(url))
  const boundaryGeoJson = parseBoundaryGeoJson(report)

  return {
    latitude,
    longitude,
    boundaryGeoJson,
    googlePlacesId: firstNonEmpty(report?.googlePlacesId),
    websites,
    wikipediaUrl,
    websiteVendor: firstNonEmpty(report?.websiteVendor),
    submitReportEmail: firstNonEmpty(report?.submitReportEmail),
    generalContacts: (report?.generalContacts || [])
      .map((contact) => ({
        name: firstNonEmpty(contact?.name),
        title: firstNonEmpty(contact?.title),
        phone: firstNonEmpty(contact?.phone),
      }))
      .filter((contact) => contact.name || contact.title || contact.phone),
    departments: (report?.departments || []).map((dept) => ({
      name: firstNonEmpty(dept?.name) || 'Unknown department',
      category: firstNonEmpty(dept?.category),
      email: firstNonEmpty(dept?.email),
      phone: firstNonEmpty(dept?.phone),
      contacts: (dept?.contacts || [])
        .map((contact) => ({
          name: firstNonEmpty(contact?.name),
          email: firstNonEmpty(contact?.email),
          phone: firstNonEmpty(contact?.phone),
        }))
        .filter(hasContactFields),
    })),
  }
}
